import { AfterViewInit, Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { SelectionChangedEvent } from 'ag-grid-community';
import * as lodash from 'lodash';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import * as _ from 'underscore';
import { isUndefined } from '../../../models/utils';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { Endpoint, EntityType, HelpIndex, SearchType } from '../../../models/enums';
import { AlignDirection, FilterTemplateType, GenericColDef, GridRowSelectionType } from '../../../models/grid-context';
import { NavigationInfo, NavigationTab } from '../../../models/navigation-info';
import { ObjectActionListType, Permissions } from '../../../models/permission';
import { SavedSearch } from '../../../models/saved-searches';
import { SearchResult } from '../../../models/search-result';
import { CommonService } from '../../../services/common/common.service';
import { XMLService } from '../../../services/xml/xml.service';
import { GenericGridComponent } from '../../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../../generic-tree/generic-tree-node';
import { ResizerComponent } from '../../resizer/resizer.component';
import { SearchComponent } from '../../search/search.component';

const autoID = 'workflow-query';

const historyColumns = new Array<GenericColDef>(
    {
        headerName: 'Search Task',
        automationId: autoID,
        lockVisible: true,
        filterType: FilterTemplateType.freeform,
        serviceTierFilter: true,
        field: 'name'
    },
    {
        headerName: 'Results',
        automationId: autoID,
        lockVisible: true,
        field: 'documentCount',
        cellAlignDirection: AlignDirection.right,
        width: 84,
        maxWidth: 90,
        minWidth: 60
    },
    {
        headerName: 'Owner',
        automationId: autoID,
        lockVisible: true,
        field: 'owner',
        filterType: FilterTemplateType.ownerSingleChoice,
        serviceTierFilter: true,
        width: 80
    }
);
const historyRequest = new DrRequestInfo({
    endpoint: Endpoint.projectManager_listSearchHistory,
    responseName: 'views',
    data: {
        filters: [{ attribute: 'docBasedResultsOnly', value: 'true', operator: 'EQUALS' }],
        selectedAttributes: [],
        startIndex: 0,
        count: 10
    }
});

const savedColumns = new Array<GenericColDef>(
    {
        headerName: 'Name',
        automationId: autoID,
        lockVisible: true,
        field: 'name'
    },
    {
        headerName: 'Search Task',
        automationId: autoID,
        lockVisible: true,
        field: 'searchQuery'
    },
    {
        headerName: 'Description',
        automationId: autoID,
        lockVisible: false,
        field: 'description',
        width: 105
    },
    {
        headerName: 'Created By',
        automationId: autoID,
        lockVisible: false,
        field: 'creator',
        width: 95
    }
);
const savedRequest = new DrRequestInfo({
    endpoint: Endpoint.projectManager_listSavedSearches,
    responseName: 'activeRules',
    data: {
        selectedAttributes: [],
        startIndex: 0,
        count: 10
    }
});


@Component({
    standalone: false,
    selector: 'res-workflow-query-modal',
    templateUrl: './workflow-query-modal.component.html',
    styleUrls: ['./workflow-query-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class WorkflowQueryModalComponent extends BaseModalComponent implements OnInit, AfterViewInit {
    @ViewChild(SearchComponent, { static: true }) searchPanel: SearchComponent;
    @ViewChild(GenericGridComponent) grid: GenericGridComponent;
    @ViewChild(ResizerComponent, { static: true }) searchBarResizer: ResizerComponent;
    modalHelpIndex = HelpIndex.myedisc_query_search_selector_workflow_dlg;
    modalTitle = 'Search Query';
    modalAutomationId = autoID;
    modalClass = 'workflow-query-modal';
    isBusy = false;
    isBusyMessage = 'Loading Searches';
    activeSearchType = SearchType.FREEFORM;
    simpleSearchSize = 90;
    summaryType: EntityType;
    navigationInfo: NavigationInfo;
    columnDefs: GenericColDef[];
    requestInfo: DrRequestInfo;
    rowSelection = GridRowSelectionType.single;
    selectedQuery: SavedSearch | SearchResult;
    rowClassRules = {
        'disabled-row': (params: any): boolean => {
            const format = _.findWhere(params.data.attributes, { name: 'resultFileFormat' });
            return (isUndefined(params.data.searchQuery) &&
                (!isUndefined(format) || (params.data.task !== SearchType.FREEFORM && params.data.task !== 'SEARCH_FROM_BULK_SEARCH')));
        }
    };

    constructor(private commonService: CommonService, private modalService: BsModalService,
        private modalRef: BsModalRef, private xmlService: XMLService) {
        super(modalService, modalRef);
        const tabs = new Array<NavigationTab>();
        if (Permissions.checkUserPermission(ObjectActionListType.AUDIT_LOG)) {
            tabs.push(new NavigationTab({ name: 'Search History', path: EntityType.profileHistory, hasPermission: Permissions.checkUserPermission(ObjectActionListType.AUDIT_LOG) }));
        }
        if (Permissions.checkUserPermission(ObjectActionListType.SAVED_SEARCH_QUERY)) {
            tabs.push(new NavigationTab({ name: 'Saved Searches', path: EntityType.savedSearch, hasPermission: Permissions.checkUserPermission(ObjectActionListType.SAVED_SEARCH_QUERY) }));
        }
        this.navigationInfo = new NavigationInfo(...tabs);
        historyRequest.data.projectHandle = this.commonService.currentProject.handle;
        savedRequest.data.projectHandle = this.commonService.currentProject.handle;
    }

    ngOnInit(): void {
        if (this.navigationInfo.tabs.length > 0) {
            this.navigateTo(<EntityType>this.navigationInfo.tabs[0].path);
        }

        this.searchPanel.selectedTreeItemChanged(new GenericTreeNode({
            data: Object.assign(this.modalData,
                {
                    attributes: [
                        { name: 'targetViewHandle', value: this.modalData.target.handle },
                        { name: 'targetViewName', value: this.modalData.target.name }
                    ]
                })
        }));
        this.searchPanel.searchTypeChangedEvent.subscribe((info: { searchType: SearchType, offset?: number, keepSelection?: boolean, resetSearchSize?: boolean }) => {
            if (info) {
                if (info.searchType !== this.activeSearchType && !info.keepSelection) {
                    this.grid.gridApi.deselectAll();
                }
                if (info.searchType === SearchType.BULK || info.searchType === SearchType.METADATA_BULK) {
                    this.searchBarResizer.collapsed = false;
                    this.searchBarResizer.collapsibleToMinSize = true;
                    this.searchBarResizer.expandableToMax = false;
                    this.searchBarResizer.minSize = 100;
                    this.searchBarResizer.initSize = 100;
                    this.searchBarResizer.usePercentages = true;
                    this.searchBarResizer.setElementSize(100);
                } else if (info.searchType === SearchType.FREEFORM) {
                    if (info.resetSearchSize) {
                        this.simpleSearchSize = 90;
                    }
                    if (info.offset) {
                        this.simpleSearchSize += info.offset;
                    }
                    this.searchBarResizer.collapsibleToMinSize = true;
                    this.searchBarResizer.expandableToMax = true;
                    this.searchBarResizer.collapsed = true;
                    this.searchBarResizer.minSize = 90;
                    this.searchBarResizer.maxSize = 300;
                    this.searchBarResizer.initSize = 90;
                    this.searchBarResizer.usePercentages = false;
                    this.searchBarResizer.setElementSize(this.simpleSearchSize);
                } else {
                    this.searchBarResizer.minSize = 90;
                    this.searchBarResizer.maxSize = 90;
                    this.searchBarResizer.initSize = 90;
                    this.searchBarResizer.usePercentages = false;
                    this.searchBarResizer.setElementSize(90);
                }
                this.activeSearchType = info.searchType;
            }
        });
    }

    ngAfterViewInit(): void {
        this.searchBarResizer.collapsibleToMinSize = true;
        this.searchBarResizer.expandableToMax = true;
        this.searchBarResizer.collapsed = true;
        this.searchBarResizer.minSize = 90;
        this.searchBarResizer.maxSize = 300;
        this.searchBarResizer.initSize = 90;
        this.searchBarResizer.setElementSize(this.simpleSearchSize);
    }

    isPrimaryBtnEnabled(): boolean {
        return !this.searchPanel.isSearchDisabled();
    }

    responseCallback(response: any): void {
        if (response?.activeRules) {
            _.each(response.activeRules, (value) => {
                const parser = new DOMParser;
                const xmlDoc = parser.parseFromString(value.searchQuery, "application/xml");
                const querytype = xmlDoc.documentElement.nodeName === "query" ? 'xml type' : 'string type';
                if (value.searchQuery && querytype === 'xml type') {
                    const query = this.xmlService.xmlToQueryConverter(value.searchQuery);
                    value.xmlQuery = value.searchQuery;
                    value.searchQuery = query ? query : '';
                }
            });
        } else if (response?.views) {
            _.each(response.views, (value) => {
                if (value.name) {
                    value.name = value.name.replace(/</g, '&lt;').replace(/>/g, '&gt;');
                }
            });
        }
    }

    navigateTo(type: EntityType): void {
        if (type && this.summaryType !== type) {
            this.summaryType = type;
            if (type === EntityType.profileHistory) {
                this.columnDefs = historyColumns;
                this.requestInfo = lodash.cloneDeep(historyRequest);
            } else {
                this.columnDefs = savedColumns;
                this.requestInfo = lodash.cloneDeep(savedRequest);
            }
            setTimeout(() => {
                this.grid.setColumnDefs(this.columnDefs);
                this.grid.showHeaderContextMenu = true;
                this.grid.headerContextMenuOptions = [];
                if (this.grid.gridApi) {
                    this.grid.gridApi.sizeColumnsToFit();
                }
                this.grid.loadData();
            });
        }
    }

    onSelectionChanged = (event?: SelectionChangedEvent): void => {
        if (event && event.api) {
            if (this.summaryType === EntityType.profileHistory) {
                this.selectedQuery = new SearchResult(event.api.getSelectedRows()[0]);
            } else {
                this.selectedQuery = new SavedSearch(event.api.getSelectedRows()[0]);
            }
            if (this.selectedQuery.handle) {
                this.searchPanel.selectedTreeItemChanged(new GenericTreeNode({ data: this.selectedQuery }));
            }

        }
    }

    ok(): void {
        this.isBusy = true;
        this.searchPanel.getQueryForWorkflow().then((searchStep) => {
            setTimeout(() => {
                this.closeModal(true, searchStep);
            });
        });
    }

}
